// Two-column layout with a fixed-width aside that collapses below the
// body at narrow viewports. Same pattern `DetailPage` already uses
// (container query on the surrounding Row), generalised so any page
// needing a main + aside structure can share the same responsive
// behaviour instead of inlining `display:grid; gridTemplateColumns: 1fr Npx`
// without a media-query fallback (the visible "labels wrap one word per
// line" bug on the create-circle form was the inline-grid path missing
// the collapse).
//
// Aside widths are quantised to two tiers — 320 (default) and 380 (wide)
// — each with its own container-query breakpoint so the aside collapses
// at the right moment regardless of width.

import { type ReactNode } from 'react';
import { Row, Stack } from '@leanin/ui';

import styles from './BodyWithAside.module.css';

export type BodyWithAsideProps = {
  /** Main column content. Flex-grows to fill remaining space. */
  children: ReactNode;
  /** Aside column content. Renders to the right at wide widths, below
   *  the body when the container drops past the collapse point. */
  aside: ReactNode;
  /** Aside width tier — 320 (default, matches DetailPage) or 380 for the
   *  wider editorial sidecar used by create forms. */
  asideWidth?: 320 | 380;
  /** Minimum width of the body column. Defaults to 320px — same as
   *  DetailPage. Used as the floor before the aside collapses. */
  bodyMinWidth?: number;
};

export function BodyWithAside({
  children,
  aside,
  asideWidth = 320,
  bodyMinWidth = 320,
}: BodyWithAsideProps) {
  const asideClass =
    asideWidth === 380 ? styles['asideWide'] : styles['aside'];
  return (
    <Row
      columnGap="xl"
      align="flex-start"
      wrap
      className={styles['row']}
    >
      <Stack
        gap="none"
        style={{ flex: 1, minWidth: bodyMinWidth }}
      >
        {children}
      </Stack>
      <div className={asideClass}>{aside}</div>
    </Row>
  );
}
